// @ts-nocheck
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Table, Form } from 'react-bootstrap';
import Select from 'react-select';
import { Card } from 'react-bootstrap-v5';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { numberFormat } from '../helpers/GlobalHelper';
import { postRequestOptions, getRequestOptions } from '../helpers/Fetchwrapper';
import { useLocation } from 'react-router-dom';
import { ReportSelectOption } from './LedgerStatementReport';

type ProjectOption = ReportSelectOption & { accounting_startup_date?: string | null };

const getToday = () => new Date().toISOString().split('T')[0];
const defaultProjectOption: ProjectOption = { label: 'Select Project', value: '' };

type ProfitLossNode = {
    name: string;
    amount: number;
    level: number;
    children: ProfitLossNode[];
    isLeaf: boolean;
};

type ProfitLossSection = {
    title: string;
    total: number;
    items: ProfitLossNode[];
};

type ProfitLossHeader = {
    projectName?: string | null;
    fromDate: string;
    toDate: string;
};

type ProfitLossPayload = {
    header: ProfitLossHeader;
    expenses: ProfitLossSection;
    revenues: ProfitLossSection;
    netProfit: number;
};

const ProfitAndLossReport: React.FC = () => {
    const location = useLocation();
    const query = useMemo(() => new URLSearchParams(location.search), [location.search]);

    const [loading, setLoading] = useState(false);
    const [projects, setProjects] = useState<ProjectOption[]>([defaultProjectOption]);
    const [project, setProject] = useState<ProjectOption>(defaultProjectOption);
    const [reportData, setReportData] = useState<ProfitLossPayload | null>(null);

    const [formData, setFormData] = useState({
        idProjects: query.get('idProjects') ?? '',
        fromDate: query.get('fromDate') ?? '',
        toDate: query.get('toDate') ?? getToday(),
    });

    const hasInitialQuery = useMemo(
        () => Boolean(query.get('idProjects') && query.get('toDate')),
        [query]
    );

    const autoLoadTriggeredRef = useRef(false);

    const fetchProjects = () => {
        fetch(process.env.REACT_APP_API_URL + 'lead/projects', getRequestOptions())
            .then((resp) => resp.json())
            .then((resp) => {
                const projectOptions: ProjectOption[] = (resp?.data || []).map((item: any) => ({
                    label: item.name,
                    value: item.id,
                    accounting_startup_date: item.accounting_startup_date ?? '',
                }));

                const optionsWithDefault = [defaultProjectOption, ...projectOptions];
                setProjects(optionsWithDefault);

                const queryProjectId = query.get('idProjects');
                let nextProject = defaultProjectOption;

                if (queryProjectId) {
                    nextProject =
                        optionsWithDefault.find((opt) => String(opt.value) === String(queryProjectId)) ?? defaultProjectOption;
                } else if (projectOptions.length > 0) {
                    nextProject = projectOptions[0];
                }

                setProject(nextProject);
                setFormData((prev) => ({
                    ...prev,
                    idProjects: nextProject.value !== '' ? String(nextProject.value) : '',
                    fromDate: nextProject.accounting_startup_date ?? prev.fromDate ?? '',
                }));
            })
            .catch((error) => {
                console.log(error, 'catch the hoop');
            });
    };

    useEffect(() => {
        fetchProjects();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleProjectChange = (selectedProject: ProjectOption | null) => {
        const nextProject = selectedProject ?? defaultProjectOption;
        setProject(nextProject);
        setFormData((prev) => ({
            ...prev,
            idProjects: nextProject.value !== '' ? String(nextProject.value) : '',
            fromDate: nextProject.accounting_startup_date ?? prev.fromDate ?? '',
        }));
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleViewReport = useCallback(async () => {
        if (!formData.idProjects) {
            toast.error('Project is required.', { position: 'bottom-right', autoClose: 4000 });
            return;
        }
        if (!formData.fromDate || !formData.toDate) {
            toast.error('From Date and To Date are required.', { position: 'bottom-right', autoClose: 4000 });
            return;
        }

        setLoading(true);
        setReportData(null);

        const payload: any = {
            fromDate: formData.fromDate,
            toDate: formData.toDate,
        };

        if (formData.idProjects) {
            payload.idProjects = formData.idProjects;
        }

        try {
            const response = await fetch(
                process.env.REACT_APP_API_URL + 'reports/profit-loss',
                postRequestOptions(payload)
            );
            const data = await response.json();

            if (data.success) {
                setReportData(data.data || null);
            } else {
                setReportData(null);

                if (data?.errorMessage) {
                    toast.error(data.errorMessage, { position: 'bottom-right', autoClose: 4000 });
                }

                (data?.errors || []).forEach((error: string) =>
                    toast.error(error, { position: 'bottom-right', autoClose: 4000 })
                );
            }
        } catch (error) {
            console.error('Error fetching report:', error);
            toast.error('Failed to load report. Please try again later.', {
                position: 'bottom-right',
                autoClose: 4000,
            });
        } finally {
            setLoading(false);
        }
    }, [formData]);

    useEffect(() => {
        if (
            hasInitialQuery &&
            !autoLoadTriggeredRef.current &&
            formData.idProjects &&
            formData.fromDate &&
            formData.toDate
        ) {
            handleViewReport();
            autoLoadTriggeredRef.current = true;
        }
    }, [formData.idProjects, formData.fromDate, formData.toDate, hasInitialQuery, handleViewReport]);

    const handlePrint = () => {
        if (!reportData) {
            toast.error('Please load the report before printing.', { position: 'bottom-right', autoClose: 4000 });
            return;
        }
        window.print();
    };

    const doubleUnderlineStyle = { borderBottom: '3px double #000' };

    const renderNodes = (nodes: ProfitLossNode[], parentKey: string): React.ReactNode[] => {
        if (!nodes || nodes.length === 0) {
            return [];
        }

        return nodes.flatMap((node, index) => {
            const key = `${parentKey}-${node.name}-${index}`;
            const hasChildren = node.children && node.children.length > 0;

            return [
                <tr key={key}>
                    <td className={hasChildren ? 'fw-bold' : ''} style={{ paddingLeft: `${node.level > 0 ? node.level * 20 : 15}px` }}>
                        {node.name}
                    </td>
                    <td className='text-end'>
                        {!hasChildren ? numberFormat(node.amount || 0) : ''}
                    </td>
                </tr>,
                ...renderNodes(node.children || [], key),
            ];
        });
    };

    const renderSection = (section: ProfitLossSection | undefined, type: 'expense' | 'revenue') => {
        if (!section || !section.items) {
            return null;
        }

        return (
            <>
                <tr key={`${type}-header`} className='table-light fw-bold'>
                    <td colSpan={2}>{section.title}</td>
                </tr>
                {renderNodes(section.items, type)}
                <tr key={`${type}-total`} className='fw-bold'>
                    <td>Total {section.title}</td>
                    <td className='text-end'>
                        <span style={doubleUnderlineStyle}>{numberFormat(section.total || 0)}</span>
                    </td>
                </tr>
                <tr key={`${type}-spacer`}>
                    <td colSpan={2}></td>
                </tr>
            </>
        );
    };

    const netAmount = reportData?.netProfit ?? 0;
    const netIsProfit = netAmount >= 0;

    return (
        <>
            <Card className='mb-4'>
                <Card.Body>
                    <Card.Text>
                        <div className='row'>
                            <div className='col-md-3'>
                                <Form.Group className="mb-3" controlId="project">
                                    <Form.Label>Project<span className='required'></span></Form.Label>
                                    <Select
                                        value={project}
                                        onChange={handleProjectChange}
                                        options={projects}
                                        menuPosition="fixed"
                                        styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                                        isClearable={true}
                                    />
                                </Form.Group>
                            </div>
                            <div className='col-md-2'>
                                <Form.Group className="mb-3" controlId="toDate">
                                    <Form.Label>To Date</Form.Label>
                                    <Form.Control
                                        type="date"
                                        placeholder="To Date"
                                        name='toDate'
                                        value={formData.toDate}
                                        onChange={handleChange}
                                    />
                                </Form.Group>
                            </div>
                            <div className='col-md-2'>
                                <div className="form-control-sm ps-0 mt-7">
                                    <button
                                        type='button'
                                        className='btn btn-sm btn-primary w-100 p-3'
                                        onClick={handleViewReport}
                                        disabled={loading}
                                    >
                                        {loading && (
                                            <span className="spinner-border spinner-border-sm me-2"></span>
                                        )}
                                        <span>View Report</span>
                                    </button>
                                </div>
                            </div>
                            <div className='col-md-2'>
                                <div className="form-control-sm ps-0 mt-7">
                                    <button
                                        type='button'
                                        className='btn btn-sm btn-primary w-100 p-3'
                                        onClick={handlePrint}
                                        disabled={loading}
                                    >
                                        <i className='fas fa-print me-1'></i>
                                        Print
                                    </button>
                                </div>
                            </div>
                        </div>
                    </Card.Text>
                </Card.Body>
            </Card>

            {reportData?.header && (
                <div className='mb-4 text-center'>
                    <h5 className='fw-bold mb-1'>Project: {reportData.header.projectName}</h5>
                    <div><strong>Period:</strong> {reportData.header.fromDate} to {reportData.header.toDate}</div>
                </div>
            )}

            <div className='row'>
                <div className='col-12 table-responsive'>
                    <Table bordered hover>
                        <thead className="sticky-top bg-white" style={{ zIndex: 0 }}>
                            <tr>
                                <th>Ledger</th>
                                <th className='text-end'>Dr. Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            {!reportData && (
                                <tr>
                                    <td colSpan={2} className="text-center">No Data Found</td>
                                </tr>
                            )}
                            {reportData && (
                                <>
                                    {renderSection(reportData.expenses, 'expense')}
                                    <tr>
                                        <th><strong>Ledger</strong></th>
                                        <th className='text-end'><strong>Cr. Amount</strong></th>
                                    </tr>
                                    {renderSection(reportData.revenues, 'revenue')}
                                    <tr key='net-result' className='table-light fw-bold'>
                                        <td>{netIsProfit ? 'Net Profit' : 'Net Loss'}</td>
                                        <td className='text-end'>
                                            <span style={doubleUnderlineStyle}>{numberFormat(Math.abs(netAmount))}</span>
                                        </td>
                                    </tr>
                                </>
                            )}
                        </tbody>
                    </Table>
                </div>
            </div>
        </>
    );
};

export default ProfitAndLossReport;